import { AsyncSection } from "../components/AsyncSection";
import { ButtonLink } from "../components/Button";
import { Callout } from "../components/Callout";
import { Container } from "../components/Container";
import { CountdownDisplay } from "../components/CountdownDisplay";
import { DisclosureNote } from "../components/DisclosureNote";
import { FounderInvite } from "../components/FounderInvite";
import { StandingRow } from "../components/StandingRow";
import { SectionHeading } from "../components/SectionHeading";
import { Skeleton } from "../components/Skeleton";
import { fetchTomorrowAuction } from "../api/public";
import { useApiResource } from "../hooks/useApiResource";
import { useCountdown } from "../hooks/useCountdown";
import { formatLongDate, formatMoney } from "../lib/format";
import { sellerSubmitUrl } from "../lib/seller";
import styles from "./TomorrowAuction.module.css";
import type { AuctionResponse, AuctionStanding } from "../api/types";

/**
 * Tomorrow's auction, read-only.
 *
 * Canon § 7: bids placed today compete for **tomorrow's** board, and a bid never changes
 * the board a visitor is looking at. The section says that in its heading, its lede and its
 * countdown, because it is the single fact most easily misread on the page.
 *
 * Standings are core's, computed per request from the standing bids. The cut line is drawn
 * from `withinTop10`, which core sets — the site never decides who is winning.
 *
 * Bidding itself belongs to the seller app (canon `docs/ARCHITECTURE.md` § Seller app);
 * nothing here writes.
 *
 * **The standings do not name the bidders (`WEB-011`).** They used to: every row was a
 * `LedgerRow` carrying the product name, the seller and a tracked link on the `other`
 * surface. A deal that has won nothing was therefore collecting buyer attention from the
 * board's own page, which is the exposure canon § 7 and § 8 sell for a *winning* bid — a
 * founder could be seen, and clicked, without ever clearing the auction. Each row now shows
 * the projected rank, a masked name (`src/lib/mask.ts`) and the amount, and this section
 * produces no click surface at all.
 *
 * Canon is unchanged by that. Canon § 13 does not put tomorrow's auction in the buyer's
 * list, and canon § 14 gives a founder their own bid and rank through `app.dealbid.app`,
 * which this does not touch.
 *
 * One honest limit: `contracts/API.md` § `GET /v1/auction/tomorrow` still returns the whole
 * `deal` object in every standing, so this masks a name the public API publishes. Actually
 * withholding it is a `dealbid-core` contract change and belongs to a task of its own.
 *
 * **Where the minimum bid went (`WEB-028`).** The stat row is the canvas's four figures, and
 * `Top of book` took the slot the built row had given `Minimum bid`. Canon § 7 makes the
 * daily floor real, so it is not dropped: it is stated in the "No bids yet" callout and in
 * the founder invitation beneath it — the state where a founder is actually deciding what to
 * bid — and in `app.dealbid.app`'s own bid form, which is the only place a bid is entered.
 * In the two states where a founder cannot bid at all, core returns no auction and therefore
 * no minimum, and printing one from memory would be `AGENTS.md` § 11's invented figure.
 */

/** Canon `AGENTS.md` § 5 allows polling for auction UI in V0 and rules out WebSockets. */
const REFRESH_MS = 60_000;

export function TomorrowAuction() {
  const resource = useApiResource<AuctionResponse>(fetchTomorrowAuction, {
    refreshMs: REFRESH_MS,
  });
  const ready = resource.state.status === "ready" ? resource.state : null;
  const data = ready?.data ?? null;
  const auction = data?.auction ?? null;

  // Only an open auction has a deadline worth counting down to. A finalizing or finalized
  // one has already closed, and a countdown there would be theatre.
  const countdown = useCountdown(
    auction !== null && auction.status === "open" ? auction.closesAt : null,
    data?.serverTime ?? null,
    ready?.receivedAtMs ?? null,
  );

  const dateline =
    data === null
      ? null
      : `${formatLongDate(data.displayDate)} · closes 00:00 UTC`;

  return (
    <section className={styles.section} aria-labelledby="tomorrow">
      <Container>
        <SectionHeading
          id="tomorrow"
          meta={dateline}
          title="Tomorrow’s auction"
          description="Founders are bidding for tomorrow’s positions right now. Nothing in this section changes today’s board — a winning bid buys the whole of tomorrow’s display day. Bidders are listed by their standing, not by name: a deal is named on this site once it has won a position or bought an Honorable Mention."
        />

        <AsyncSection
          state={resource.state}
          reload={resource.reload}
          loadingLabel="Loading tomorrow’s auction"
          errorTitle="Tomorrow’s auction didn’t load"
          skeleton={<AuctionSkeleton />}
        >
          {(loaded) =>
            /*
              Canon § 6 (decision `D-011`) is checked before the auction row, because the two
              say different things. A missing auction row means "not opened *yet* today"; cold
              start means no auction exists at all until the board fills, and the standings,
              the minimum bid and the invitation to bid are all meaningless until then.
            */
            !loaded.bidding.open ? (
              <Callout title="Bidding hasn’t started yet">
                Dealbid is still filling its first{" "}
                {String(loaded.bidding.eligibleDealsRequired - 1)} positions. Until then every
                approved deal is listed free, in the order it was approved, and no daily
                auction runs. Bidding opens once there are more deals than the board can hold.
              </Callout>
            ) : loaded.auction === null ? (
              <Callout title="Tomorrow’s auction hasn’t opened yet">
                A new auction opens when today’s board is finalized. Bids for{" "}
                {formatLongDate(loaded.displayDate)} can be placed from then on.
              </Callout>
            ) : (
              <>
                {/*
                  The canvas's stat row: four figures on one line in the mono face, then the
                  action. Each is a fact core returned — canon § 11 rules out manufactured
                  urgency, so the countdown states when bidding closes rather than pressing.
                */}
                <div className={styles.statsRow}>
                  <dl className={styles.stats}>
                    <div className={styles.stat}>
                      <dt className={styles.statLabel}>Auction closes</dt>
                      <dd className={styles.statFigure}>
                        {loaded.auction.status === "open" &&
                        countdown !== null ? (
                          <CountdownDisplay
                            countdown={countdown}
                            absolute={`00:00 UTC on ${formatLongDate(loaded.displayDate)}`}
                            expiredLabel="Closed — finalizing"
                          />
                        ) : (
                          <span>Closed — finalizing</span>
                        )}
                      </dd>
                    </div>
                    {/*
                      The canvas's second figure: **Top of book** — the highest standing bid
                      for tomorrow's board (`WEB-028`). The built row showed `Minimum bid`
                      here, which is not what the drawing carries.

                      Read as the amount of the standing core projects at rank 1, never as a
                      maximum this site computed: `contracts/API.md`
                      § `GET /v1/auction/tomorrow` returns the standings already ordered by
                      projected rank, and the discipline of this whole section is that the
                      order is core's and the site never re-ranks anything.

                      `WEB-011` is not reopened by printing it. That task masks a bidder's
                      *name*; a top-of-book figure names nobody, and the standings below
                      already print every amount beside a masked name.
                    */}
                    <div className={styles.stat}>
                      <dt className={styles.statLabel}>Top of book</dt>
                      <dd className={`${styles.statFigure} figure`}>
                        {loaded.auction.standings[0] === undefined ? (
                          // Not `$0` — that is a bid nobody placed. Canon `AGENTS.md` § 11
                          // rules out invented figures, and a zero here would be one.
                          <span className={styles.statEmpty}>No bids yet</span>
                        ) : (
                          formatMoney(loaded.auction.standings[0].amountCents)
                        )}
                      </dd>
                    </div>
                    <div className={styles.stat}>
                      <dt className={styles.statLabel}>Bids placed</dt>
                      <dd className={`${styles.statFigure} figure`}>
                        {String(loaded.auction.capacity.standingBids)}
                      </dd>
                    </div>
                    <div className={styles.stat}>
                      <dt className={styles.statLabel}>Positions</dt>
                      <dd className={`${styles.statFigure} figure`}>
                        {String(loaded.auction.capacity.top10)}
                      </dd>
                    </div>
                  </dl>
                  {/*
                  Outside the `dl`, not inside it: a definition list may only hold its own
                  term-and-description groups, and axe is right to refuse a button parked
                  among them.

                  The canvas labels this "Place a bid". Canon § 14 puts bidding behind a
                  founder's Google session, which a public page has no way to hold — and a
                  founder cannot bid at all until a deal of theirs has been approved. So the
                  button keeps its place and its weight but names where it actually goes: to
                  `app.dealbid.app`, to sign in and submit (`D-008`, `WEB-014`). A label that
                  promised a bid form and delivered a sign-in would be the kind of small lie
                  this board exists not to tell.
                */}
                  <ButtonLink
                    className={styles.action}
                    href={sellerSubmitUrl()}
                  >
                    Submit a deal to bid
                  </ButtonLink>
                </div>

                <DisclosureNote disclosure={loaded.disclosure} />

                {loaded.auction.standings.length === 0 ? (
                  // The same two audiences as the empty board (`WEB-016`): the fact for the
                  // buyer, then the ask for the founder who can act on it. Approval comes
                  // first here — canon § 7 admits a bid only from an approved deal — so the
                  // lead says so rather than implying a bid is the way in.
                  <>
                    <Callout title="No bids yet for tomorrow">
                      The first qualifying bid at{" "}
                      {formatMoney(loaded.auction.minBidCents)} takes position 1.
                    </Callout>
                    <FounderInvite
                      lead={
                        <>
                          Bidding is open to founders whose deal Dealbid has already
                          approved, so approval is the first step and{" "}
                          {formatMoney(loaded.auction.minBidCents)} is the whole of the
                          second.
                        </>
                      }
                    />
                  </>
                ) : (
                  <Standings standings={loaded.auction.standings} />
                )}
              </>
            )
          }
        </AsyncSection>
      </Container>
    </section>
  );
}

/**
 * The standings, split at the cut line.
 *
 * Two lists rather than one with a marker row: the bids below the line are genuinely a
 * different set — they are in the auction but are not currently winning anything — and a
 * separate list lets the boundary be stated in words above it. Canon
 * `contracts/DESIGN-TOKENS.md` forbids status by colour alone, so the dimming below the
 * line is never the only thing that says so.
 */
function Standings({ standings }: { standings: AuctionStanding[] }) {
  const winning = standings.filter((standing) => standing.withinTop10);
  const below = standings.filter((standing) => !standing.withinTop10);

  return (
    <>
      <ol className={styles.standings} role="list">
        {winning.map((standing) => (
          <StandingRow
            key={standing.deal.id}
            rank={standing.projectedRank}
            productName={standing.deal.productName}
            figure={formatMoney(standing.amountCents)}
            figureLabel="current bid"
          />
        ))}
      </ol>

      {below.length === 0 ? null : (
        <>
          <p className={styles.cutLine}>
            Top 10 cut line — the bids below are in the auction but would not
            win a position if bidding closed now.
          </p>
          <ol
            className={styles.standings}
            role="list"
            start={winning.length + 1}
          >
            {below.map((standing) => (
              <StandingRow
                key={standing.deal.id}
                rank={standing.projectedRank}
                productName={standing.deal.productName}
                figure={formatMoney(standing.amountCents)}
                figureLabel="current bid"
                muted
              />
            ))}
          </ol>
        </>
      )}
    </>
  );
}

function AuctionSkeleton() {
  return (
    <div className={styles.skeleton}>
      <Skeleton width="45%" height="1.5rem" />
      {[0, 1, 2, 3].map((row) => (
        <Skeleton key={row} height="1.25rem" />
      ))}
    </div>
  );
}
